import { Injectable, inject, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {SummaryService} from '../../../services/summary.service';
import {PreferenceService} from '../../../services/preference.service';
import {Store} from '@ngrx/store';
import * as fromStore from '../../../store';
import {Observable, of} from 'rxjs';
import * as featureActions from '../../index';
import {catchError, combineLatest, map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators';
import { SummaryActions } from '../actions/summary.actions';
import {Preference, Summary} from '../../summary/summary.model';
import { UpdateInfoModel } from '../../updateInfo/update-info.model';
import { updateInfoType, AuthOtpType } from '../../../shareModel';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SummaryEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<fromStore.State>);
  private summaryService = inject(SummaryService);
  private preferenceService = inject(PreferenceService);
  private injector = inject(Injector);


  constructor() {}

  ReadAccountPreference$ = createEffect(() => this.actions$.pipe(
    ofType(featureActions.SummaryActionTypes.GetPreferences),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]) => {
      return this.preferenceService.ReadAccountPreference(user.otp).pipe(
        map((res: any) => {
          if (res.msg.body) {
            const preference: Preference = res.msg.body;
            return new featureActions.UpsertPreferences({ preferences: preference });
          } else {
            return new featureActions.NoPreferencesInfo();
          }
        }),
        catchError((error) => {
          return of(new featureActions.GetPreferencesFail());
        })
      );
    })
  ));
  UpdateAccountPreference$ = createEffect(() => this.actions$.pipe(
    ofType(featureActions.SummaryActionTypes.UpdatePreferences),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      const params = {
        ...action.payload,
        otp: user.otp,
        acode: user.acode
      };
      const updateRes: UpdateInfoModel = {
        result: false,
        msg: '',
        type: action.payload.CompanionReceipt === undefined ? updateInfoType.UpdatePreference : updateInfoType.UpdateCompanionReceipt
      };
      return this.preferenceService.UpdateAccountPreference(params).pipe(
        switchMap((res: any) => {
          if (!res.Flag) {
            return [new featureActions.NoPreferencesInfo(),
                    new featureActions.UpdateInfoFailAction({ ...updateRes, errmsg: res.msg.body })];
          } else {
            return [new featureActions.GetPreferences(),
            new featureActions.UpdateInfoSuccessAction({ ...updateRes, msg: this.injector.get(TranslateService).instant('Update Preferences Success'), result: true })];
          }
        }),
        catchError((error) => {
          return [new featureActions.GetPreferencesFail(),
                  new featureActions.UpdateInfoFailAction({ ...updateRes, errmsg: error })];
        })
      );
    })
  ));
  UpdateAccountPreferenceWithAuthOTP$ = createEffect(() => this.actions$.pipe(
    ofType(featureActions.SummaryActionTypes.UpdateAccountPreferenceWithAuthOTP),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      const payload = { ...action.payload, otp: user.otp };
      const updateRes: UpdateInfoModel = {
        result: false,
        msg: '',
        type: payload.AuthType === AuthOtpType.SMSSetting
          ? updateInfoType.SMSSetting
          : updateInfoType.CompanionEnable
      }
      return this.preferenceService.UpdateAccountPreferenceWithAuthOTP(payload).pipe(
        switchMap((res: any) => {
          if (!res.Flag) {
            return [
              new featureActions.UpdateAccountPreferenceWithAuthOTPFail(),
              new featureActions.UpdateInfoSuccessAction({ ...updateRes, errmsg: res.msg.body })];
          } else {
            const msg = payload.AuthType === AuthOtpType.CompanionEnable
              ? this.injector.get(TranslateService).instant('PPC Companion Enable Success')
              : payload.AuthType === AuthOtpType.CompanionDisable
                ? this.injector.get(TranslateService).instant('PPC Companion Disable Success')
                : payload.AuthType === AuthOtpType.SMSSetting
                  ? this.injector.get(TranslateService).instant('Your account mobile has been changed.')
                  : updateRes.msg;
            return [
              new featureActions.GetPreferences(),
              new featureActions.UpdateInfoSuccessAction({ ...updateRes, result: true, msg })];
          }
        }),
        catchError((error) => {
          return [
            new featureActions.UpdateAccountPreferenceWithAuthOTPFail(),
            new featureActions.UpdateInfoSuccessAction({ ...updateRes, errmsg: error })];
        })
      );
    })
  ));
  ReadEventSummary$ = createEffect(() => this.actions$.pipe(
    ofType(featureActions.SummaryActionTypes.GetSummary),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      let param = {}
      if (action.payload !== null && action.payload.RegionIds !== null) {
        param = {
          otp: user.otp,
          RegionIds: action.payload.RegionIds
        };
      } else {
        param = {
          otp: user.otp
        };
      }
      return this.summaryService.ReadEventSummary(param).pipe(
        map((res: any) => {
          if (res.msg.body) {
            const summary: Summary = {
              weekly_data: res.msg.body.weekly_data,
              monthly_data: res.msg.body.monthly_data,
            };
            return new featureActions.UpsertSummary(res.msg.body);
          } else {
            return new featureActions.NoPreferencesInfo();
          }
        }),
        // TODO: catchError Message調整 --> 留待未來再回頭定義Error Message
        catchError((error) => {
          return of(new featureActions.GetPreferencesFail());
        })
      );
    })
  ));
  getSummaryDetail$ = createEffect(() => this.actions$.pipe(
    ofType(featureActions.SummaryActionTypes.GetSummaryDetail),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap( ([action, currentUser]: [any, any]) => {
        const payload = currentUser 
          ? { ...action.payload, otp: currentUser.otp }
          : { ...action.payload };
        return this.summaryService.getSummaryDetail(payload).pipe(
          map((res: any) => {
            if (res.result) {
              return new featureActions.GetSummaryDetailSuccess(res.msg.body);
            } else {
              return new featureActions.GetSummaryDetailFail(res);
            }
          }),
          // TODO: catchError Message調整 --> 留待未來再回頭定義Error Message
          catchError(error => of(new featureActions.GetSummaryDetailFail()))
        );
    })
  ));
  DeleteMobile$ = createEffect(() => this.actions$.pipe(
    ofType(featureActions.SummaryActionTypes.DeleteMobile),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      const updateRes: UpdateInfoModel = {
        result: false,
        msg: '',
        type: updateInfoType.DeleteMobile
      };
      const param = {
        otp: user.otp,
        From: action.payload.From
      };
      return this.preferenceService.DeleteMobile(param).pipe(
        switchMap((res: any) => {
          if (!res.Flag) {
            return [
              new featureActions.DeleteMobileFail(),
              new featureActions.UpdateInfoSuccessAction({ ...updateRes, errmsg: res.msg.body })];
          } else {
            return [
              new featureActions.GetPreferences(),
              new featureActions.UpdateInfoSuccessAction({ ...updateRes, msg: this.injector.get(TranslateService).instant('Your account mobile has been removed.'), result: true })];
          }
        }),
        catchError((error) => {
          return [
            new featureActions.DeleteMobileFail(),
            new featureActions.UpdateInfoSuccessAction({ ...updateRes, errmsg: error })];
        })
      );
    })
  ));
}
